// One planner for the dry-run (rule editor) and the real execution (tick): spec 05 §7, §10.8.
// Payload shapes are the contract consumed by SQL apply_db_action and the edge executors.
import {
  nextCallingWindow,
  withinCallingHours,
} from "../gavion/time/callingHours.ts";
import { BUSINESS_TZ } from "../gavion/time/torontoTime.ts";
import { DEAL_PROPOSAL_COLUMNS } from "../gavion/dealProposal.ts";
import { evaluateConditions } from "./conditions.ts";
import { getPath, renderTemplate } from "./templates.ts";
import {
  calendarOf,
  callbackUsesDelay,
  resolveTime,
  timeSpecOf,
  type PlannerConfig,
  type TimeResult,
} from "./delays.ts";
import {
  EDGE_ACTIONS,
  type ActionSpec,
  type ActionType,
  type AutomationEvent,
  type AutomationRule,
  type TraceEntry,
} from "./types.ts";

export type PlannedAction = {
  index: number;
  type: ActionType;
  runsIn: "db" | "edge";
  dueAt: string | null;
  payload: Record<string, unknown>;
  status: "planned" | "skipped";
  reason?: string;
  warnings: string[];
};
export type RulePlan = {
  ruleId: number;
  ruleKey: string | null;
  matched: boolean;
  trace: TraceEntry[];
  actions: PlannedAction[];
};
export type PlanEvent = Pick<
  AutomationEvent,
  "event_id" | "entity_type" | "entity_id" | "context" | "created_at"
>;
export type PlanOptions = { now: Date; config: PlannerConfig };

type Built = { payload: Record<string, unknown>; skip?: string };
type Env = {
  ctx: Record<string, unknown>;
  rule: AutomationRule;
  ev: PlanEvent;
  opts: PlanOptions;
  /** Base instant for relative times: the action's dueAt, else the event's created_at. */
  anchor: Date;
  warnings: string[];
};

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v)
    ? v
    : typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))
      ? Number(v)
      : null;
const orNull = (v: unknown): unknown => v ?? null;

const isTimeZone = (tz: unknown): tz is string => {
  if (typeof tz !== "string" || !tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

function timeOf(a: ActionSpec, env: Env): TimeResult {
  const spec =
    timeSpecOf(a, env.opts.config.callback_default_time) ??
    // Ruling: a task without `due` is still wanted, due at the anchor time.
    (a.type === "create_task" ? { minutes: 0 } : null);
  if (!spec) return { at: null, skip: "time_source_missing" };
  const cal = calendarOf(env.opts.config);
  return resolveTime(spec, env.ctx, env.opts.now, cal, env.anchor);
}

/** A callback must land inside calling hours, in the lead's zone (else the default zone). */
function inCallingHours(at: Date, env: Env): Date | null {
  const hours = env.opts.config.calling_hours;
  const zone = getPath(env.ctx, "company.timezone");
  const fallback = isTimeZone(hours?.timezone_default)
    ? hours.timezone_default
    : BUSINESS_TZ;
  if (zone !== undefined && zone !== null && zone !== "" && !isTimeZone(zone))
    env.warnings.push("invalid_timezone");
  const tz = isTimeZone(zone) ? zone : fallback;
  try {
    if (withinCallingHours(at, tz, hours)) return at;
    env.warnings.push("shifted_to_calling_hours");
    return nextCallingWindow(at, tz, hours);
  } catch {
    return null;
  }
}

/**
 * The context with deal.amount blanked (GAVION deal amount visibility, 72_), and the deal's « Forfait proposé » too
 * (GAVION deal package, 76_: its package and MRR give the amount away).
 */
export const withoutDealAmount = (ctx: unknown): unknown => {
  if (!ctx || typeof ctx !== "object") return ctx;
  const deal = (ctx as Record<string, unknown>).deal;
  if (
    !deal ||
    typeof deal !== "object" ||
    !["amount", ...DEAL_PROPOSAL_COLUMNS].some((k) => k in deal)
  )
    return ctx;
  const masked: Record<string, unknown> = { ...(deal as object) };
  for (const k of ["amount", ...DEAL_PROPOSAL_COLUMNS])
    if (k in masked) masked[k] = null;
  return { ...(ctx as object), deal: masked };
};

function buildPayload(a: ActionSpec, env: Env): Built {
  const { ctx, rule, ev } = env;
  const get = (path: string) => getPath(ctx, path);
  // GAVION deal amount visibility (72_): a template never prints deal.amount — its text lands in tasks, notes, deal
  // names, notifications and e-mails a Setter / Apporteur only may read; conditions still compare it (server side).
  // Review fix: a notification for the admins only (notify_user who "admins": their own notifications rows) keeps it.
  const renderCtx = withoutDealAmount(ctx);
  const render = (tpl: unknown, adminsOnly = false) => {
    const r = renderTemplate(String(tpl ?? ""), adminsOnly ? ctx : renderCtx);
    env.warnings.push(...r.missing.map((v) => `missing_variable:${v}`));
    return r.text;
  };
  const company_id = num(get("company.id"));
  const deal_id = num(get("deal.id")) ?? num(get("call.deal_id"));
  const rep_id = num(get("rep.id"));
  const call_id = num(get("call.id"));
  const contact_id = num(get("contact.id"));
  const needCompany = (payload: Record<string, unknown>): Built =>
    company_id === null ? { payload, skip: "no_company" } : { payload };

  switch (a.type) {
    case "set_callback": {
      const t = timeOf(a, env);
      if (!t.at) return { payload: {}, skip: t.skip };
      const at = inCallingHours(t.at, env);
      if (!at) return { payload: {}, skip: "invalid_time" };
      return needCompany({
        company_id,
        at: at.toISOString(),
        from_event_at: ev.created_at,
        call_id,
      });
    }
    case "set_prospect_status":
      return needCompany({ company_id, status: a.status });
    case "cool_down":
      return needCompany({ company_id, days: a.days });
    case "create_task": {
      const t = timeOf(a, env);
      if (!t.at) return { payload: {}, skip: t.skip };
      const assignee = a.assignee ?? "rep";
      const sales_id =
        assignee === "rep"
          ? rep_id
          : typeof assignee === "number"
            ? assignee
            : null;
      const assign_to =
        assignee === "rep" ? "rep" : assignee === "admin" ? "admin" : "user";
      return needCompany({
        company_id,
        deal_id,
        sales_id,
        assign_to,
        task_type: a.task_type ?? "call",
        text: render(a.text),
        due_at: t.at.toISOString(),
        rule_id: rule.id,
      });
    }
    case "create_deal": {
      const amount = a.amount_from ? (num(get(String(a.amount_from))) ?? 0) : 0;
      return needCompany({
        company_id,
        sales_id: rep_id,
        stage: a.stage,
        name: render(a.name_template),
        amount,
        call_id,
        demo_at: orNull(get("call.demo_at")),
      });
    }
    case "move_deal":
      return deal_id === null
        ? { payload: {}, skip: "no_deal" }
        : { payload: { deal_id, stage: a.stage } };
    case "add_note":
      return needCompany({
        company_id,
        text: render(a.text_template),
        sales_id: rep_id,
      });
    case "enroll_sequence": {
      const email = get("contact.email") || get("company.email");
      if (typeof email !== "string" || !email)
        return { payload: {}, skip: "no_email" };
      return needCompany({
        sequence_id: a.sequence_id,
        company_id,
        contact_id,
        email,
        rule_id: rule.id,
      });
    }
    case "stop_sequences":
      return needCompany({ company_id, reason: render(a.reason) });
    case "send_email": {
      const to = get(String(a.to));
      if (typeof to !== "string" || !to)
        return { payload: {}, skip: "no_email" };
      return {
        payload: {
          template: a.template,
          via: a.via,
          to,
          company_id,
          contact_id,
          deal_id,
          sales_id: rep_id,
          language: get("company.language") ?? "fr",
        },
      };
    }
    case "create_calendar_event":
      return {
        payload: {
          call_id,
          deal_id,
          sales_id: rep_id,
          demo: orNull(get(String(a.from))),
        },
      };
    case "notify_teams":
      return {
        payload: {
          template: a.template,
          payload: {
            company: { id: company_id, name: orNull(get("company.name")) },
            rep: {
              id: rep_id,
              first_name: orNull(get("rep.first_name")),
              last_name: orNull(get("rep.last_name")),
            },
            deal: {
              id: deal_id, // GAVION (72_ owner decision): no amount in a Teams post (the whole team reads it)
              stage: get("deal.stage") ?? orNull(get("to_stage")),
            },
            demo_at: get("call.demo_at") ?? orNull(get("deal.demo_at")),
          },
        },
      };
    case "notify_user":
      return {
        payload: {
          who: a.who,
          sales_id: a.who === "rep" ? rep_id : null,
          title: render(a.message_template, a.who === "admins"),
          link: company_id !== null ? `/companies/${company_id}/show` : null,
        },
      };
    case "suppress": {
      const type = get("type");
      return needCompany({
        channels: a.channels,
        phone: orNull(get("company.phone_e164")),
        email:
          get("message.to_emails.0") ||
          get("contact.email") ||
          get("company.email") ||
          null,
        company_id,
        reason:
          type === "complained"
            ? "complaint"
            : type === "bounced"
              ? "bounce"
              : "manual",
      });
    }
    // GAVION ai-email (spec §5): queue a proposed draft; the SQL side resolves the recipient and the eligibility.
    case "ai_draft": {
      const trigger = String(a.trigger ?? "");
      if (trigger === "stale_lead") return { payload: { trigger } };
      const source =
        trigger === "after_call"
          ? { source_type: "call", source_id: call_id }
          : trigger === "client_reply"
            ? {
                source_type: "email_message",
                source_id: num(get("message.id")),
              }
            : trigger === "after_demo"
              ? { source_type: "meeting_note", source_id: num(get("note.id")) }
              : null;
      if (!source) return { payload: {}, skip: "unknown_ai_trigger" };
      if (source.source_id === null) return { payload: {}, skip: "no_source" };
      return needCompany({
        trigger,
        company_id,
        contact_id:
          trigger === "after_call" ? num(get("call.contact_id")) : contact_id,
        deal_id,
        ...source,
      });
    }
    default:
      return { payload: {}, skip: "unknown_action" };
  }
}

const parseInstant = (iso: unknown): Date | null => {
  const t = typeof iso === "string" ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(t) ? new Date(t) : null;
};

function planAction(
  raw: unknown,
  index: number,
  base: Omit<Env, "anchor" | "warnings">,
  eventAt: Date,
): PlannedAction {
  const { ctx, opts } = base;
  if (
    !raw ||
    typeof raw !== "object" ||
    typeof (raw as ActionSpec).type !== "string"
  ) {
    const type = (raw as { type?: unknown } | null)?.type;
    return {
      index,
      type: (typeof type === "string" ? type : "unknown") as ActionType,
      runsIn: "db",
      dueAt: null,
      payload: {},
      status: "skipped",
      reason: "unknown_action",
      warnings: [],
    };
  }
  const a = raw as ActionSpec;
  const runsIn = EDGE_ACTIONS.includes(a.type) ? "edge" : "db";
  const common = { index, type: a.type, runsIn } as const;
  let dueAt: Date | null = null;
  if (a.delay && !callbackUsesDelay(a)) {
    const cal = calendarOf(opts.config);
    const d = resolveTime(a.delay, ctx, opts.now, cal, eventAt);
    if (!d.at)
      return {
        ...common,
        dueAt: null,
        payload: {},
        status: "skipped",
        reason: d.skip,
        warnings: [],
      };
    dueAt = d.at;
  }
  const env: Env = { ...base, anchor: dueAt ?? eventAt, warnings: [] };
  const { payload, skip } = buildPayload(a, env);
  return skip
    ? {
        ...common,
        dueAt: null,
        payload,
        status: "skipped",
        reason: skip,
        warnings: env.warnings,
      }
    : {
        ...common,
        dueAt: dueAt?.toISOString() ?? null,
        payload,
        status: "planned",
        warnings: env.warnings,
      };
}

/** A rule that cannot run: not matched, with the reason as a failed trace entry. */
const rejected = (
  ruleId: number,
  ruleKey: string | null,
  reason: string,
): RulePlan => ({
  ruleId,
  ruleKey,
  matched: false,
  trace: [
    {
      field: reason,
      op: "exists",
      expected: true,
      actual: null,
      result: false,
    },
  ],
  actions: [],
});

/** Evaluates the rule against the event and plans each action. Pure: no I/O, `now` injected. */
export function planRule(
  rule: AutomationRule,
  ev: PlanEvent,
  opts: PlanOptions,
): RulePlan {
  if (!rule || typeof rule !== "object" || Array.isArray(rule))
    return rejected(0, null, "<invalid_rule>");
  const id = typeof rule.id === "number" ? rule.id : 0;
  const key = typeof rule.key === "string" ? rule.key : null;
  // Ruling: a rule without conditions never fires; `{}` is the explicit "always".
  if (rule.conditions === null || rule.conditions === undefined)
    return rejected(id, key, "<no_conditions>");
  // The runner's config always wins, so `config.max_attempts` refs see the live setting.
  const ctx: Record<string, unknown> = { ...ev.context, config: opts.config };
  const { matched, trace } = evaluateConditions(rule.conditions, ctx, opts.now);
  const base = { ruleId: id, ruleKey: key, matched, trace };
  if (!matched) return { ...base, actions: [] };
  // Relative delays count from the event, so a replay or a late tick plans the same instants.
  const eventAt = parseInstant(ev.created_at) ?? opts.now;
  const list: unknown[] = Array.isArray(rule.actions) ? rule.actions : [];
  const actions = list.map((a, index) =>
    planAction(a, index, { ctx, rule, ev, opts }, eventAt),
  );
  return { ...base, actions };
}
